/**
 * RemoveConfirmModal — confirm перед cascade-remove aggregate chip (§10.3).
 *
 * Block M1a-FE (TZ 2026-07-29). State-driven через props (parent page
 * держит `pendingChip` и `onConfirm`).
 *
 * Count source of truth:
 *   - BEFORE confirm: optimistic = `chip.row_count` (from `aggregate`,
 *     computed by BE).
 *   - AFTER confirm: `remove.data?.affected_row_count` (BE cascade result).
 *     Displayed in success toast by the parent; here we only render the
 *     pre-confirm dialog.
 */
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import { Modal } from '@/components/ui/Modal';
import type { AggregateChipData } from './AggregateChips';
import { useRemoveCategory } from '@/hooks/useMatching';

export interface RemoveConfirmModalProps {
  chip: AggregateChipData | null;
  pipelineId: string | null;
  onClose: () => void;
}

export function RemoveConfirmModal({
  chip,
  pipelineId,
  onClose,
}: RemoveConfirmModalProps) {
  const { t } = useTranslation();
  const remove = useRemoveCategory();

  function handleConfirm() {
    if (!chip || !pipelineId) return;
    remove.mutate(
      { pipeline_id: pipelineId, category_key: chip.key },
      {
        onSettled: () => {
          onClose();
        },
      },
    );
  }

  // `chip.row_count` — best-effort before API responds. After success, the
  // parent shows `remove.data?.affected_row_count` via toast/console.
  const previewCount = chip?.row_count ?? 0;

  return (
    <Modal
      open={Boolean(chip)}
      onClose={onClose}
      title={t('process.matching.results.removeConfirm.title', {
        defaultValue: 'Remove category?',
      })}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary text-xs px-3 py-1.5"
            disabled={remove.isPending}
          >
            {t('process.matching.results.removeConfirm.cancel', {
              defaultValue: 'Cancel',
            })}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={remove.isPending || !chip || !pipelineId}
            className="btn-danger text-xs px-3 py-1.5"
          >
            {t('process.matching.results.removeConfirm.remove', {
              defaultValue: 'Remove',
            })}
          </button>
        </>
      }
    >
      <div className="flex items-start gap-3 py-1">
        <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
        <p className="text-sm text-surface-200 leading-snug">
          {t('process.matching.results.removeConfirm.body', {
            defaultValue:
              'This will clear category matches for {n} rows and move them to unresolved.',
            n: previewCount,
          })}
        </p>
      </div>
    </Modal>
  );
}
